import { useEffect, useRef } from 'react';
import {
  entityKey,
  setActiveNotificationTarget,
  useMarkNotificationViewedByTarget,
  useNotificationsByEntity,
} from '../../notifications/index.js';

// Lead-drawer auto mark-viewed (#1363). Mirrors the request-drawer
// `useAutoMarkNotificationsViewed` (apps/web/src/features/notifications/
// hooks/useAutoMarkNotificationsViewed.ts) but the lead drawer is
// single-pane: the only notification type is `lead_callback` bound to the
// `LEAD` entity, so there is no per-tab fan-out. The by-target path
// accepts (LEAD, lead_callback) since #1380.
const LEAD_VIEWING = new Set<'lead_callback'>(['lead_callback']);

/**
 * @aiPurpose drawer-mount auto mark-viewed for the lead callback
 *   notification (#1363). Two responsibilities, mirroring the request
 *   drawer:
 *   1. publish the active (LEAD, leadId, {lead_callback}) target so the
 *      realtime handler can optimistically clear an incoming aggregate
 *      before the row dot flashes;
 *   2. fire `mark-viewed-by-target` on mount. Fires eagerly even when no
 *      aggregate is cached yet so the backend records a viewed marker for
 *      the race-safe UPSERT.
 * @aiInvariant a per-leadId activation guard prevents tight repeats on
 *   every render. A realtime event surfacing a new aggregate id re-arms
 *   via the cursor below.
 */
export function useAutoMarkLeadCallbackViewed(leadId: string | null) {
  const { mutate } = useMarkNotificationViewedByTarget();
  const { byEntity } = useNotificationsByEntity();

  // Activation guard + newest-aggregate cursor (per leadId), matching the
  // request-drawer hook's re-arm semantics.
  const activatedRef = useRef(false);
  const seenCursorRef = useRef<string | null>(null);

  // 1. Publish active target for the realtime handler.
  useEffect(() => {
    if (!leadId) return;
    setActiveNotificationTarget({
      entityType: 'LEAD',
      entityId: leadId,
      tabsViewing: LEAD_VIEWING,
    });
    return () => {
      setActiveNotificationTarget(null);
    };
  }, [leadId]);

  // 2. Eager fire on mount; re-fire when a new aggregate id appears.
  useEffect(() => {
    if (!leadId) return;
    const summary = byEntity.get(entityKey('LEAD', leadId));
    const matches =
      summary?.unreadAggregates.filter((r) => r.notificationType === 'lead_callback') ?? [];
    const newest =
      matches.length === 0
        ? null
        : matches.reduce((a, b) =>
            Date.parse(a.lastEventAt) >= Date.parse(b.lastEventAt) ? a : b,
          );

    const cursorAdvanced = newest !== null && seenCursorRef.current !== newest.id;
    const firstActivation = !activatedRef.current;
    if (!cursorAdvanced && !firstActivation) return;

    if (newest !== null) seenCursorRef.current = newest.id;
    activatedRef.current = true;
    mutate({ entityType: 'LEAD', entityId: leadId, notificationType: 'lead_callback' });
  }, [byEntity, leadId, mutate]);

  // 3. Reset the activation guard when the lead changes — a different
  //    drawer must re-fire on its own first mount.
  useEffect(() => {
    if (!leadId) return;
    return () => {
      activatedRef.current = false;
      seenCursorRef.current = null;
    };
  }, [leadId]);
}
